// ─── SystemOverview ──────────────────────────────────────────────────────────
//
// Section wrapper that frames the existing Health Pillars grid with:
//   - a section header: "Your Health Systems"
//   - a status summary chip (e.g. "3 of 6 systems nominal * 1 needs
//     attention * 2 incomplete data")
//   - data completeness + freshness metadata (e.g. "Data from Apr 12 *
//     11/14 signals complete")
//
// This component is composition-only. It does NOT reimplement the pillar
// grid — callers pass the existing PillarGrid (or any equivalent) as
// children. All presentation data comes from the typed view model
// (SystemOverview type); no business logic in JSX.
// ──────────────────────────────────────────────────────────────────────────────

import React from 'react';
import type {
  SystemOverview as SystemOverviewVM,
  SeverityBand,
  DataFreshness,
} from '../../lib/dashboard/types';

interface Props {
  /** Full section view model produced by buildDashboardVM(). */
  data: SystemOverviewVM;
  /** The existing PillarGrid (or any grid component) passed as children. */
  children: React.ReactNode;
  /**
   * Optional refresh callback. When provided, SystemOverview shows a
   * small inline refresh control next to the trust chip. This absorbs
   * the old standalone DashboardSyncStatus row so the hero area stays
   * compact.
   */
  onRefresh?: () => void;
  /** Whether a refresh is currently in flight (spins the icon). */
  refreshing?: boolean;
}

// ─── Visual mappings ─────────────────────────────────────────────────────────

/** Summary chip color by overall band. */
const BAND_CHIP: Record<SeverityBand, { bg: string; text: string; dot: string }> = {
  excellent: { bg: 'bg-teal-50 dark:bg-teal-900/30', text: 'text-teal-700 dark:text-teal-300', dot: 'bg-teal-500' },
  good:      { bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', dot: 'bg-green-500' },
  fair:      { bg: 'bg-amber-50 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  attention: { bg: 'bg-red-50 dark:bg-red-900/30',     text: 'text-red-700 dark:text-red-300',     dot: 'bg-red-500' },
  no_data:   { bg: 'bg-gray-50 dark:bg-white/5',       text: 'text-gray-600 dark:text-gray-400',    dot: 'bg-gray-400' },
};

/** Freshness warning label when data is stale. */
const FRESHNESS_WARNING: Partial<Record<DataFreshness, string>> = {
  stale: 'Data may be a few days old',
  very_stale: 'Data is more than a week old',
  missing: 'No recent data available',
};

// ─── Main component ──────────────────────────────────────────────────────────

export default function SystemOverview({ data, children, onRefresh, refreshing = false }: Props) {
  const { statusSummary, trust } = data;
  const chip = BAND_CHIP[statusSummary.overallBand];
  const freshnessWarning = FRESHNESS_WARNING[trust.freshness];

  const headingId = 'system-overview-heading';
  const summaryId = 'system-overview-summary';

  return (
    <section
      aria-labelledby={headingId}
      aria-describedby={summaryId}
      className="card"
      data-phase="7"
      data-testid="system-overview"
    >
      {/* Header row: title + trust chip + inline refresh (absorbs old
          standalone DashboardSyncStatus so the hero stays compact) */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0 flex-1">
          <h2
            id={headingId}
            className="section-label text-arete-forest dark:text-gray-200"
          >
            Your Health Systems
          </h2>
          <p
            id={summaryId}
            className={`mt-1 text-xs font-medium ${chip.text}`}
          >
            <span
              className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle ${chip.dot}`}
              aria-hidden="true"
            />
            {statusSummary.text}
          </p>
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          {/* Trust metadata chip */}
          <div className={`rounded-full px-3 py-1 text-[11px] font-medium ${chip.bg} ${chip.text}`}>
            Data from {trust.lastUpdatedDateLabel}
            <span className="mx-1.5 opacity-50" aria-hidden="true">·</span>
            {trust.signalsAvailable}/{trust.signalsExpected} signals
          </div>

          {/* Inline refresh — absorbs the old DashboardSyncStatus row */}
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              aria-label={refreshing ? 'Refreshing data' : 'Refresh data'}
              aria-live="polite"
              className="text-[11px] text-arete-blue hover:text-arete-navy dark:hover:text-gray-200 disabled:text-arete-muted motion-reduce:transition-none transition-colors flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-arete-blue/40 rounded px-1.5 py-0.5"
            >
              <span className={refreshing ? 'animate-spin inline-block' : 'inline-block'} aria-hidden="true">↺</span>
              <span className="hidden sm:inline">{refreshing ? 'Refreshing…' : 'Refresh'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Freshness warning row (only when data is stale or missing) */}
      {freshnessWarning && (
        <p className="mb-3 text-[11px] text-arete-muted italic">
          {freshnessWarning}
        </p>
      )}

      {/* Existing pillar grid (or any children) */}
      <div className="mt-3">
        {children}
      </div>
    </section>
  );
}
